/**
 * GeographyAdminModal — System-level admin interface for managing the global
 * geographic community hierarchy.
 *
 * Accessible from the gear menu (top-right).  Only system admins should have
 * access.  Manages geo_country → geo_state → geo_city entities and their
 * parent/child relationships, independent of any single community.
 *
 * Active geos generate community pages; inactive geos are location labels only.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import SlugField from "../Shared/SlugField";
import { normalizeGuid, guidEquals } from "../../utils/guid";
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  createCommunityEntity,
  updateCommunityEntity,
  createRelationship,
  removeRelationship,
} from "../../api";
import type {
  CommunityEntityResponse,
  CommunityEntityRelationshipResponse,
} from "../../api";

/* ================================================================
   Constants
   ================================================================ */
const GEO_TYPES = [
  "geo_planet",
  "geo_country",
  "geo_state",
  "geo_region",
  "geo_city",
  "geo_district",
  "geo_neighborhood",
] as const;

function typeLabel(t: string): string {
  return t
    .replace(/^geo_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function depthColor(t: string): string {
  switch (t) {
    case "geo_planet":       return "#8b5cf6"; // violet
    case "geo_country":      return "#6366f1"; // indigo
    case "geo_state":        return "var(--accent-primary)"; // blue
    case "geo_region":       return "#0ea5e9"; // sky
    case "geo_city":         return "#14b8a6"; // teal
    case "geo_district":     return "#22c55e"; // green
    case "geo_neighborhood": return "#84cc16"; // lime
    default:                 return "var(--text-muted)"; // slate
  }
}

/* ================================================================
   Styles (inline, dark theme — matches app)
   ================================================================ */
const S = {
  overlay: {
    position: "fixed" as const, inset: 0, zIndex: 8000,
    background: "rgba(0,0,0,0.65)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  modal: {
    background: "var(--bg-primary)",
    border: "1px solid var(--border-default)",
    borderRadius: 14,
    width: "90vw", maxWidth: 900,
    height: "85vh",
    display: "flex" as const, flexDirection: "column" as const,
    boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
    overflow: "hidden" as const,
  },
  header: {
    display: "flex" as const, alignItems: "center" as const, justifyContent: "space-between" as const,
    padding: "16px 20px",
    borderBottom: "1px solid var(--border-subtle)",
  },
  title: { fontSize: 17, fontWeight: 700, color: "var(--text-primary)" },
  subtitle: { fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 },
  closeBtn: {
    background: "none", border: "none", color: "var(--text-muted)",
    fontSize: 20, cursor: "pointer", padding: "4px 8px", lineHeight: 1,
  },
  body: {
    flex: 1, overflowY: "auto" as const, padding: "16px 20px",
    display: "flex" as const, flexDirection: "column" as const, gap: 12,
  },
  toast: (ok: boolean) => ({
    padding: "8px 14px", borderRadius: 8, fontSize: 13,
    background: ok ? "var(--success-bg)" : "var(--error-bg)",
    border: `1px solid ${ok ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
    color: ok ? "#4ade80" : "#f87171",
  }),
  toolbar: {
    display: "flex" as const, gap: 8, alignItems: "center" as const,
  },
  input: {
    flex: 1, padding: "7px 12px", fontSize: 13, borderRadius: 8,
    border: "1px solid var(--border-default)", background: "var(--bg-hover-strong)",
    color: "var(--text-primary)", outline: "none",
  },
  btnPrimary: {
    padding: "7px 16px", fontSize: 13, fontWeight: 600, borderRadius: 8,
    border: "none", cursor: "pointer",
    background: "var(--accent-primary)", color: "var(--accent-text)",
  },
  btnMini: {
    padding: "4px 10px", fontSize: 12, borderRadius: 6,
    border: "1px solid var(--border-default)", background: "var(--bg-hover-strong)",
    color: "var(--text-tertiary)", cursor: "pointer",
  },
  select: {
    padding: "7px 10px", fontSize: 13, borderRadius: 8,
    border: "1px solid var(--border-default)", background: "var(--bg-hover-strong)",
    color: "var(--text-primary)", outline: "none",
  },
  label: { fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 },
  field: { display: "flex" as const, flexDirection: "column" as const, marginBottom: 8 },
  formBox: {
    background: "var(--bg-hover)", borderRadius: 10, padding: 16,
    border: "1px solid var(--border-default)",
  },
  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: 13 },
  th: {
    textAlign: "left" as const, padding: "8px 12px", fontSize: 11, fontWeight: 600,
    color: "var(--text-muted)", borderBottom: "1px solid var(--border-subtle)",
    textTransform: "uppercase" as const, letterSpacing: "0.04em",
  },
  td: {
    padding: "6px 12px", borderBottom: "1px solid var(--border-subtle)",
  },
  confirmOverlay: {
    position: "fixed" as const, inset: 0, zIndex: 9999,
    display: "flex" as const, alignItems: "center" as const, justifyContent: "center" as const,
    background: "rgba(0,0,0,0.6)",
  },
  confirmBox: {
    background: "var(--bg-secondary)", border: "1px solid var(--border-default)",
    borderRadius: 12, padding: "20px 24px", maxWidth: 420, width: "90%",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
};

/* ================================================================
   Component
   ================================================================ */
interface Props {
  open: boolean;
  onClose: () => void;
  /** Render as inline page content (no overlay/modal chrome). Default: "modal". */
  mode?: "modal" | "inline";
}

export default function GeographyAdminModal({ open, onClose, mode = "modal" }: Props) {
  const { t } = useAppTranslation("geography");
  const [entities, setEntities] = useState<CommunityEntityResponse[]>([]);
  const [relationships, setRelationships] = useState<CommunityEntityRelationshipResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Create / Edit form
  const [editing, setEditing] = useState<{
    entityGuid: string | null;
    name: string;
    entityType: string;
    visibility: string;
    code: string;
    isActive: boolean;
    slug: string; // DEV-939 — empty string = unset
  } | null>(null);
  const [parentGuid, setParentGuid] = useState<string>("");

  // Edit-parent
  const [editingParent, setEditingParent] = useState<{
    entityGuid: string;
    currentParentGuid: string | null;
    newParentGuid: string;
  } | null>(null);

  // Typeahead state for parent pickers
  const [parentQuery, setParentQuery] = useState("");
  const [showParentSuggestions, setShowParentSuggestions] = useState(false);
  const [moveParentQuery, setMoveParentQuery] = useState("");
  const [showMoveParentSuggestions, setShowMoveParentSuggestions] = useState(false);

  // Confirmation dialog
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Auto-dismiss success
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  /* ── data loading ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ents, rels] = await Promise.all([
        fetchAllCommunityEntitiesAdmin().catch(() => [] as CommunityEntityResponse[]),
        fetchAllRelationships().catch(() => [] as CommunityEntityRelationshipResponse[]),
      ]);
      // Filter to geo-only
      setEntities(ents.filter((e) => e.entityType.startsWith("geo_")));
      setRelationships(rels);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  /* ── derived data ── */
  const entityByGuid = useMemo(() => {
    const m = new Map<string, CommunityEntityResponse>();
    for (const e of entities) m.set(normalizeGuid(e.entityGuid), e);
    return m;
  }, [entities]);

  const childrenByParent = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of relationships) {
      if (r.relationshipType !== "parent_of" && r.relationshipType !== "contains") continue;
      // Only include if both parent & child are geo entities in our set
      if (!entityByGuid.has(normalizeGuid(r.parentEntityGuid))) continue;
      if (!entityByGuid.has(normalizeGuid(r.childEntityGuid))) continue;
      const list = m.get(normalizeGuid(r.parentEntityGuid)) || [];
      list.push(normalizeGuid(r.childEntityGuid));
      m.set(normalizeGuid(r.parentEntityGuid), list);
    }
    return m;
  }, [relationships, entityByGuid]);

  const parentOfChild = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of relationships) {
      if (r.relationshipType !== "parent_of" && r.relationshipType !== "contains") continue;
      if (entityByGuid.has(normalizeGuid(r.childEntityGuid)) && entityByGuid.has(normalizeGuid(r.parentEntityGuid))) {
        m.set(normalizeGuid(r.childEntityGuid), normalizeGuid(r.parentEntityGuid));
      }
    }
    return m;
  }, [relationships, entityByGuid]);

  /** Build display path for a geo entity: "Leaf, Parent, Grandparent" (no Earth) */
  const getGeoPath = useCallback((guid: string): string => {
    const parts: string[] = [];
    const seen = new Set<string>();
    let cur = normalizeGuid(guid);
    for (let i = 0; i < 20; i++) {
      if (!cur || seen.has(cur)) break;
      seen.add(cur);
      const ent = entityByGuid.get(cur);
      if (ent) {
        const n = ent.name.trim();
        if (n.toLowerCase() !== "earth") parts.push(n);
      }
      cur = parentOfChild.get(cur) || "";
    }
    return parts.join(", ");
  }, [entityByGuid, parentOfChild]);

  /** Searchable index of all geo entities with display paths */
  const geoSearchIndex = useMemo(() => {
    const out: { guid: string; display: string; search: string; leafName: string; entityType: string }[] = [];
    for (const e of entities) {
      const display = getGeoPath(e.entityGuid);
      if (!display) continue;
      out.push({
        guid: e.entityGuid,
        display,
        search: `${e.name} ${display}`.toLowerCase(),
        leafName: e.name,
        entityType: e.entityType,
      });
    }
    out.sort((a, b) => a.display.localeCompare(b.display));
    return out;
  }, [entities, getGeoPath]);

  /** Filter geo index by query string with scoring */
  const filterGeoSuggestions = useCallback((query: string, excludeGuid?: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as { guid: string; display: string; entityType: string }[];
    const scored: { guid: string; display: string; entityType: string; score: number }[] = [];
    for (const row of geoSearchIndex) {
      if (excludeGuid && row.guid === excludeGuid) continue;
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      const depthBoost = Math.min(20, row.display.split(",").length);
      scored.push({ guid: row.guid, display: row.display, entityType: row.entityType, score: score + depthBoost });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12);
  }, [geoSearchIndex]);

  // Suggestions for the create-form parent picker
  const parentSuggestions = useMemo(
    () => filterGeoSuggestions(parentQuery),
    [parentQuery, filterGeoSuggestions]
  );

  const rootEntities = useMemo(() => {
    const childGuids = new Set<string>();
    childrenByParent.forEach((children) => {
      children.forEach((c) => childGuids.add(normalizeGuid(c)));
    });
    return entities.filter((e) => !childGuids.has(normalizeGuid(e.entityGuid)));
  }, [entities, childrenByParent]);

  const filteredEntities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null; // use hierarchy view
    return entities.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.code.toLowerCase().includes(q) ||
        e.entityType.toLowerCase().includes(q)
    );
  }, [entities, search]);

  const expandGeo = (guid: string) => {
    setExpanded((prev) => {
      if (prev.has(guid)) return prev;
      const next = new Set(prev);
      next.add(guid);
      return next;
    });
  };

  const collapseGeo = (guid: string) => {
    setExpanded((prev) => {
      if (!prev.has(guid)) return prev;
      const next = new Set(prev);
      next.delete(guid);
      return next;
    });
  };

  /* ── CRUD handlers ── */
  const startCreate = (parentEntityGuid?: string) => {
    // Default type: auto-detect from parent depth
    let defaultType = "geo_planet";
    if (parentEntityGuid) {
      const parentEnt = entityByGuid.get(normalizeGuid(parentEntityGuid));
      if (parentEnt) {
        const typeMap: Record<string, string> = {
          geo_planet: "geo_country",
          geo_country: "geo_state",
          geo_state: "geo_city",
          geo_region: "geo_city",
          geo_city: "geo_district",
          geo_district: "geo_neighborhood",
        };
        defaultType = typeMap[parentEnt.entityType] || "geo_city";
      }
    }
    setEditing({
      entityGuid: null,
      name: "",
      entityType: defaultType,
      visibility: "public",
      code: "",
      isActive: false, // default: location only until explicitly activated
      slug: "",
    });
    setParentGuid(parentEntityGuid || "");
    setError(null);
  };

  const startEdit = (e: CommunityEntityResponse) => {
    setEditing({
      entityGuid: e.entityGuid,
      name: e.name,
      entityType: e.entityType,
      visibility: e.visibility,
      code: e.code,
      isActive: e.isActive,
      slug: e.slug ?? "",
    });
    setParentGuid("");
    setError(null);
  };

  const doSaveEntity = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      if (editing.entityGuid) {
        await updateCommunityEntity(editing.entityGuid, {
          name: editing.name.trim(),
          entityType: editing.entityType,
          visibility: editing.visibility,
          code: editing.code.trim(),
          isActive: editing.isActive,
          // DEV-939: send slug as-is. Empty string clears; non-empty triggers
          // server-side validation (charset, reserved word, per-immediate-
          // parent uniqueness for geo entities).
          slug: editing.slug.trim(),
        });
        setSuccessMsg(`"${editing.name.trim()}" updated.`);
      } else {
        await createCommunityEntity({
          name: editing.name.trim(),
          entityType: editing.entityType,
          visibility: editing.visibility || "public",
          code: editing.code.trim(),
          parentEntityGuid: parentGuid || undefined,
          relationshipType: parentGuid ? "parent_of" : undefined,
          slug: editing.slug.trim() || undefined,
        });
        setSuccessMsg(`"${editing.name.trim()}" created.`);
      }
      setEditing(null);
      setParentQuery("");
      setShowParentSuggestions(false);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEntity = () => {
    if (!editing) return;
    if (!editing.name.trim()) { setError("Name is required."); return; }

    const nameLower = editing.name.trim().toLowerCase();
    const duplicate = entities.find(
      (e) => e.name.toLowerCase() === nameLower && normalizeGuid(e.entityGuid) !== normalizeGuid(editing.entityGuid)
    );

    const isCreate = !editing.entityGuid;
    const parentName = parentGuid ? entityByGuid.get(normalizeGuid(parentGuid))?.name : null;
    const activeLabel = editing.isActive ? "Active (community page)" : "Inactive (location only)";
    const typeStr = typeLabel(editing.entityType);

    const lines: string[] = [];
    if (isCreate) {
      lines.push(`Create "${editing.name.trim()}" as a ${typeStr}?`);
      if (parentName) lines.push(`Parent: ${parentName}`);
      lines.push(`Status: ${activeLabel}`);
      if (duplicate) lines.push(`\u26A0 A geography named "${duplicate.name}" already exists. Are you sure this isn't a duplicate?`);
    } else {
      lines.push(`Update "${editing.name.trim()}"?`);
      lines.push(`Status: ${activeLabel}`);
    }

    setConfirm({
      title: isCreate ? "Confirm New Geography" : "Confirm Update",
      message: lines.join("\n"),
      onConfirm: doSaveEntity,
    });
  };

  const toggleActive = (e: CommunityEntityResponse) => {
    const willActivate = !e.isActive;
    setConfirm({
      title: willActivate ? "Activate Geography" : "Deactivate Geography",
      message: willActivate
        ? `Activating "${e.name}" will generate a public community page for this geographic area. Members will be able to join and content will be visible. Continue?`
        : `Deactivating "${e.name}" will remove its community page. It will still appear as a location label in dropdowns. Continue?`,
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          await updateCommunityEntity(e.entityGuid, { isActive: !e.isActive });
          setSuccessMsg(`"${e.name}" is now ${willActivate ? "active (community page)" : "location only"}.`);
          await loadData();
        } catch (err: any) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveParentChange = async () => {
    if (!editingParent) return;
    const { entityGuid, currentParentGuid, newParentGuid } = editingParent;
    setSaving(true);
    setError(null);
    try {
      if (currentParentGuid) {
        await removeRelationship(currentParentGuid, entityGuid, "parent_of").catch(() =>
          removeRelationship(currentParentGuid, entityGuid, "contains")
        );
      }
      if (newParentGuid) {
        await createRelationship({
          parentEntityGuid: newParentGuid,
          childEntityGuid: entityGuid,
          relationshipType: "parent_of",
        });
      }
      const entName = entityByGuid.get(normalizeGuid(entityGuid))?.name || entityGuid;
      const newParentName = newParentGuid ? (entityByGuid.get(normalizeGuid(newParentGuid))?.name || "?") : "root (no parent)";
      setSuccessMsg(`"${entName}" moved to ${newParentName}.`);
      setEditingParent(null);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hideEntity = (e: CommunityEntityResponse) => {
    const childCount = (childrenByParent.get(e.entityGuid) || []).length;
    const msg = childCount > 0
      ? `This will hide "${e.name}" and deactivate its community page. It has ${childCount} sub-geograph${childCount === 1 ? "y" : "ies"} — they will NOT be hidden automatically. Continue?`
      : `This will hide "${e.name}" — it will be deactivated (no community page) and set to private visibility. Continue?`;

    setConfirm({
      title: "Hide Geography",
      message: msg,
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          await updateCommunityEntity(e.entityGuid, { isActive: false, visibility: "private" });
          setSuccessMsg(`"${e.name}" is now hidden.`);
          await loadData();
        } catch (err: any) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const unhideEntity = (e: CommunityEntityResponse) => {
    setConfirm({
      title: "Unhide Geography",
      message: `This will restore "${e.name}" — it will be reactivated with a community page and set to public visibility. Continue?`,
      onConfirm: async () => {
        setSaving(true);
        setError(null);
        try {
          await updateCommunityEntity(e.entityGuid, { isActive: true, visibility: "public" });
          setSuccessMsg(`"${e.name}" is now visible.`);
          await loadData();
        } catch (err: any) {
          setError(err.message);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  /* ── render helpers ── */
  const renderRow = (e: CommunityEntityResponse, depth: number): React.ReactNode => {
    const nGuid = normalizeGuid(e.entityGuid);
    const children = childrenByParent.get(nGuid) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(nGuid);
    const rowOpacity = e.isActive ? 1 : 0.55;
    const tc = depthColor(e.entityType);

    return (
      <React.Fragment key={e.entityGuid}>
        <tr style={{ opacity: rowOpacity }}>
          {/* Name */}
          <td style={{ ...S.td, paddingInlineStart: 12 + depth * 24 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => isExpanded ? collapseGeo(nGuid) : expandGeo(nGuid)}
                  style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 11, width: 16, padding: 0 }}
                >
                  {isExpanded ? "\u25BC" : "\u25B6"}
                </button>
              ) : (
                <span style={{ width: 16, display: "inline-block" }} />
              )}
              <strong style={{ color: "var(--text-primary)" }}>{e.name}</strong>
              {e.code && <span style={{ opacity: 0.45, fontSize: 11, marginInlineStart: 4 }}>{e.code}</span>}
              {/* DEV-939 — slug pill so admins can see at a glance which geo
                  entities have URL slugs set. Click 'Edit' to change. */}
              {e.slug && (
                <span
                  title={`URL slug: ${e.slug}`}
                  style={{
                    display: "inline-flex", alignItems: "center",
                    fontSize: 10, fontWeight: 600,
                    padding: "1px 6px", borderRadius: 4,
                    background: "rgba(99,102,241,0.16)",
                    border: "1px solid rgba(99,102,241,0.35)",
                    color: "#c7d2fe",
                    marginInlineStart: 4,
                    fontFamily: "var(--font-mono, ui-monospace, monospace)",
                    letterSpacing: 0.2,
                  }}
                >
                  /{e.slug}
                </span>
              )}
            </span>
          </td>

          {/* Type */}
          <td style={S.td}>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 8,
              background: tc, color: "#fff",
            }}>
              {typeLabel(e.entityType)}
            </span>
          </td>

          {/* Status */}
          <td style={S.td}>
            <button
              type="button"
              onClick={() => toggleActive(e)}
              disabled={saving}
              title={e.isActive ? t("activeTitle") : t("inactiveTitle")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: e.isActive ? "rgba(34,197,94,0.18)" : "var(--bg-hover-strong)",
                color: e.isActive ? "#4ade80" : "var(--text-muted)",
                border: `1px solid ${e.isActive ? "rgba(34,197,94,0.3)" : "var(--border-default)"}`,
                borderRadius: 6, padding: "2px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer",
              }}
            >
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: e.isActive ? "#4ade80" : "var(--text-muted)",
                display: "inline-block",
              }} />
              {e.isActive ? "Active" : "Location Only"}
            </button>
          </td>

          {/* Children count */}
          <td style={{ ...S.td, color: "var(--text-muted)", fontSize: 11 }}>
            {hasChildren ? `${children.length} sub` : "—"}
          </td>

          {/* Actions */}
          <td style={{ ...S.td, whiteSpace: "nowrap" }}>
            <button type="button" style={{ ...S.btnMini, marginInlineEnd: 4 }} onClick={() => startEdit(e)} title={t("editBtn")}>
              {t("editBtn")}
            </button>
            <button type="button" style={{ ...S.btnMini, marginInlineEnd: 4 }} onClick={() => {
              const cp = parentOfChild.get(nGuid) || null;
              setEditingParent({ entityGuid: e.entityGuid, currentParentGuid: cp, newParentGuid: cp || "" });
            }} title={t("moveBtn")}>
              {t("moveBtn")}
            </button>
            <button type="button" style={{ ...S.btnMini, marginInlineEnd: 4 }} onClick={() => startCreate(e.entityGuid)} title={t("addChild")}>
              + Sub
            </button>
            {e.isActive ? (
              <button type="button" style={{ ...S.btnMini, color: "#f87171", borderColor: "rgba(239,68,68,0.3)" }} onClick={() => hideEntity(e)} title={t("hideBtn")}>
                {t("hideBtn")}
              </button>
            ) : (
              <button type="button" style={{ ...S.btnMini, color: "#4ade80", borderColor: "rgba(34,197,94,0.3)" }} onClick={() => unhideEntity(e)} title={t("unhideBtn")}>
                {t("unhideBtn")}
              </button>
            )}
          </td>
        </tr>

        {/* Edit-parent inline */}
        {guidEquals(editingParent?.entityGuid, e.entityGuid) && (() => {
          const moveSuggestions = filterGeoSuggestions(moveParentQuery, e.entityGuid);
          const selectedMoveName = editingParent.newParentGuid
            ? (getGeoPath(editingParent.newParentGuid) || entityByGuid.get(normalizeGuid(editingParent.newParentGuid))?.name || "")
            : "";
          return (
            <tr>
              <td colSpan={5} style={{ padding: "8px 16px 8px " + (36 + depth * 24) + "px" }}>
                <div style={{
                  background: "rgba(99,102,241,0.08)", padding: "10px 12px", borderRadius: 8,
                  fontSize: 12, border: "1px solid rgba(99,102,241,0.2)",
                }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>{t("moveTo")}</span>
                    <button type="button" style={{ ...S.btnPrimary, fontSize: 12, padding: "4px 12px" }}
                      onClick={saveParentChange}
                      disabled={saving || editingParent.newParentGuid === (editingParent.currentParentGuid || "")}>
                      Save
                    </button>
                    <button type="button" style={S.btnMini} onClick={() => { setEditingParent(null); setMoveParentQuery(""); setShowMoveParentSuggestions(false); }}>Cancel</button>
                  </div>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                      background: "var(--bg-hover-strong)", border: "1px solid var(--border-default)",
                      borderRadius: 8, padding: "4px 8px", minHeight: 32,
                    }}>
                      {editingParent.newParentGuid ? (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "rgba(99,102,241,0.16)", border: "1px solid rgba(99,102,241,0.35)",
                          borderRadius: 6, padding: "2px 8px", fontSize: 12, color: "#c7d2fe",
                          maxWidth: "100%", overflow: "hidden",
                        }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {selectedMoveName}
                          </span>
                          <button type="button" onClick={() => { setEditingParent({ ...editingParent, newParentGuid: "" }); setMoveParentQuery(""); }}
                            style={{ background: "none", border: "none", color: "#c7d2fe", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                            aria-label={t("clearParent")}>&times;</button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--text-muted)", padding: "2px 0" }}>{t("noParent")}</span>
                      )}
                      <input
                        style={{ flex: 1, minWidth: 100, background: "transparent", border: "none", color: "var(--text-primary)", fontSize: 12, padding: "3px 0", outline: "none" }}
                        value={moveParentQuery}
                        placeholder={editingParent.newParentGuid ? t("typeToChange") : t("typeToSearch")}
                        onChange={(ev) => {
                          if (editingParent.newParentGuid && ev.target.value.trim()) {
                            setEditingParent({ ...editingParent, newParentGuid: "" });
                          }
                          setMoveParentQuery(ev.target.value);
                          setShowMoveParentSuggestions(true);
                        }}
                        onFocus={() => setShowMoveParentSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowMoveParentSuggestions(false), 200)}
                        disabled={saving}
                      />
                    </div>
                    {showMoveParentSuggestions && moveSuggestions.length > 0 && (
                      <div style={{
                        position: "absolute", top: "100%", insetInline: 0, zIndex: 60,
                        background: "var(--bg-secondary)", border: "1px solid var(--border-default)", borderRadius: 8,
                        marginTop: 4, maxHeight: 200, overflowY: "auto",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                      }}>
                        <div
                          onMouseDown={() => { setEditingParent({ ...editingParent, newParentGuid: "" }); setMoveParentQuery(""); setShowMoveParentSuggestions(false); }}
                          style={{ padding: "6px 12px", fontSize: 12, color: "var(--text-muted)", cursor: "pointer", borderBottom: "1px solid var(--border-subtle)", fontStyle: "italic" }}
                          onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "rgba(99,102,241,0.12)"; }}
                          onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                        >
                          — {t("noParent")} —
                        </div>
                        {moveSuggestions.map((s) => (
                          <div key={s.guid}
                            onMouseDown={() => { setEditingParent({ ...editingParent, newParentGuid: s.guid }); setMoveParentQuery(""); setShowMoveParentSuggestions(false); }}
                            style={{ padding: "6px 12px", fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", borderBottom: "1px solid var(--border-subtle)" }}
                            onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "rgba(99,102,241,0.12)"; }}
                            onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                          >
                            <span>{s.display}</span>
                            <span style={{ marginInlineStart: 6, fontSize: 10, opacity: 0.5 }}>({typeLabel(s.entityType)})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          );
        })()}

        {/* Children */}
        {isExpanded &&
          children
            .map((cg) => entityByGuid.get(normalizeGuid(cg)))
            .filter(Boolean)
            .sort((a, b) => a!.name.localeCompare(b!.name))
            .map((child) => renderRow(child!, depth + 1))}
      </React.Fragment>
    );
  };

  if (!open) return null;

  /* ── Shared body content (used by both modal and inline modes) ── */
  const bodyContent = (
        <div style={mode === "inline" ? { padding: 0 } : S.body}>
          {/* Toasts */}
          {successMsg && <div style={S.toast(true)}>{successMsg}</div>}
          {error && <div style={S.toast(false)}>{error}</div>}

          {/* Confirmation dialog */}
          {confirm && (
            <div style={S.confirmOverlay} onClick={() => setConfirm(null)}>
              <div style={S.confirmBox} onClick={(ev) => ev.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, color: "var(--text-primary)" }}>
                  {confirm.title}
                </div>
                <div style={{ fontSize: 13, color: "var(--text-tertiary)", whiteSpace: "pre-line", lineHeight: 1.5, marginBottom: 16 }}>
                  {confirm.message}
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" style={{ ...S.btnMini, padding: "6px 16px", fontSize: 13 }} onClick={() => setConfirm(null)}>
                    Cancel
                  </button>
                  <button type="button" style={{ ...S.btnPrimary, padding: "6px 16px", fontSize: 13 }}
                    onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                    disabled={saving}>
                    {saving ? "Saving..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div style={S.toolbar}>
            <input
              style={S.input}
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              placeholder={t("search")}
            />
            <button type="button" style={S.btnPrimary} onClick={() => startCreate()}>
              + New Geography
            </button>
          </div>

          {/* Create / Edit form */}
          {editing && (
            <div style={S.formBox}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: "var(--text-primary)" }}>
                {editing.entityGuid ? "Edit Geography" : "New Geography"}
                {parentGuid && (
                  <span style={{ fontWeight: 400, opacity: 0.6, marginInlineStart: 8, fontSize: 12 }}>
                    child of {entityByGuid.get(normalizeGuid(parentGuid))?.name || parentGuid}
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div style={S.field}>
                  <span style={S.label}>{t("form.nameRequired")}</span>
                  <input style={{ ...S.input, flex: "unset" }} value={editing.name}
                    onChange={(ev) => setEditing({ ...editing, name: ev.target.value })}
                    placeholder="e.g. United States, Texas, Houston" disabled={saving} autoFocus />
                </div>
                <div style={S.field}>
                  <span style={S.label}>{t("form.type")}</span>
                  <select style={{ ...S.select }} value={editing.entityType}
                    onChange={(ev) => setEditing({ ...editing, entityType: ev.target.value })} disabled={saving}>
                    {GEO_TYPES.map((t) => (
                      <option key={t} value={t}>{typeLabel(t)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                <div style={S.field}>
                  <span style={S.label}>{t("form.code")}</span>
                  <input style={{ ...S.input, flex: "unset" }} value={editing.code}
                    onChange={(ev) => setEditing({ ...editing, code: ev.target.value })}
                    placeholder="e.g. US, US-TX, US-TX-HOU" disabled={saving} />
                </div>
                <div style={S.field}>
                  <span style={S.label}>{t("form.visibility")}</span>
                  <select style={S.select} value={editing.visibility}
                    onChange={(ev) => setEditing({ ...editing, visibility: ev.target.value })} disabled={saving}>
                    <option value="public">{t("form.public")}</option>
                    <option value="private">{t("form.private")}</option>
                  </select>
                </div>
                <div style={S.field}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={S.label}>{t("form.status")}</span>
                    <span style={{ fontSize: 10, opacity: 0.4 }}>
                      {editing.isActive ? "Generates community page" : "Location label only"}
                    </span>
                  </div>
                  <select style={S.select} value={editing.isActive ? "active" : "inactive"}
                    onChange={(ev) => setEditing({ ...editing, isActive: ev.target.value === "active" })} disabled={saving}>
                    <option value="active">{t("form.activeCommunityPage")}</option>
                    <option value="inactive">{t("form.inactiveLocationOnly")}</option>
                  </select>
                </div>
              </div>
              {/* DEV-939 — URL slug editor + live preview. Sysadmin can claim
                  a top-level country slug (e.g. /usa); admins of geo entities
                  can curate slugs in their subtree. Per-immediate-parent
                  uniqueness — siblings under different parents may share a
                  slug, the URL path itself disambiguates. */}
              <SlugField
                value={editing.slug}
                name={editing.name}
                onChange={(v) => setEditing({ ...editing, slug: v })}
                parentContextGuid={
                  editing.entityGuid
                    ? parentOfChild.get(normalizeGuid(editing.entityGuid))
                    : parentGuid || undefined
                }
                entityName={editing.name}
                disabled={saving}
              />
              {!editing.entityGuid && (
                <div style={S.field}>
                  <span style={S.label}>{t("form.parentGeography")}</span>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                      ...S.input, padding: "4px 8px", minHeight: 34, flex: "unset",
                    }}>
                      {parentGuid && (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "rgba(99,102,241,0.16)", border: "1px solid rgba(99,102,241,0.35)",
                          borderRadius: 6, padding: "2px 8px", fontSize: 12, color: "#c7d2fe",
                          maxWidth: "100%", overflow: "hidden",
                        }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {getGeoPath(parentGuid) || entityByGuid.get(normalizeGuid(parentGuid))?.name || parentGuid}
                          </span>
                          <button type="button" onClick={() => { setParentGuid(""); setParentQuery(""); setShowParentSuggestions(false); }}
                            style={{ background: "none", border: "none", color: "#c7d2fe", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                            aria-label={t("form.clearParent")}>&times;</button>
                        </div>
                      )}
                      <input
                        style={{ flex: 1, minWidth: 100, background: "transparent", border: "none", color: "var(--text-primary)", fontSize: 12, padding: "3px 0", outline: "none" }}
                        value={parentQuery}
                        placeholder={parentGuid ? t("typeToChange") : t("typeToSearch")}
                        onChange={(ev) => {
                          if (parentGuid && ev.target.value.trim()) setParentGuid("");
                          setParentQuery(ev.target.value);
                          setShowParentSuggestions(true);
                        }}
                        onFocus={() => setShowParentSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowParentSuggestions(false), 200)}
                        disabled={saving}
                      />
                    </div>
                    {showParentSuggestions && parentSuggestions.length > 0 && (
                      <div style={{
                        position: "absolute", top: "100%", insetInline: 0, zIndex: 60,
                        background: "var(--bg-secondary)", border: "1px solid var(--border-default)", borderRadius: 8,
                        marginTop: 4, maxHeight: 200, overflowY: "auto",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                      }}>
                        {parentSuggestions.map((s) => (
                          <div key={s.guid}
                            onMouseDown={() => { setParentGuid(s.guid); setParentQuery(""); setShowParentSuggestions(false); }}
                            style={{ padding: "6px 12px", fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", borderBottom: "1px solid var(--border-subtle)" }}
                            onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "rgba(99,102,241,0.12)"; }}
                            onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                          >
                            <span>{s.display}</span>
                            <span style={{ marginInlineStart: 6, fontSize: 10, opacity: 0.5 }}>({typeLabel(s.entityType)})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" style={S.btnPrimary} onClick={saveEntity} disabled={saving}>
                  {saving ? "Saving..." : editing.entityGuid ? "Update" : "Create"}
                </button>
                <button type="button" style={S.btnMini} onClick={() => setEditing(null)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", opacity: 0.6 }}>{t("loading")}</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{t("table.name")}</th>
                    <th style={{ ...S.th, width: 120 }}>{t("table.type")}</th>
                    <th style={{ ...S.th, width: 130 }}>{t("table.status")}</th>
                    <th style={{ ...S.th, width: 70 }}>{t("table.children")}</th>
                    <th style={{ ...S.th, width: 240 }}>{t("table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntities ? (
                    filteredEntities.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, textAlign: "center", padding: 24 }}>
                          <span style={{ opacity: 0.6 }}>{t("noMatches")}</span>
                        </td>
                      </tr>
                    ) : (
                      filteredEntities.sort((a, b) => a.name.localeCompare(b.name)).map((e) => renderRow(e, 0))
                    )
                  ) : rootEntities.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ ...S.td, textAlign: "center", padding: 24 }}>
                        <span style={{ opacity: 0.6 }}>
                          No geographies found. Click <b>+ New Geography</b> to create one (e.g. Earth, United States).
                        </span>
                      </td>
                    </tr>
                  ) : (
                    rootEntities.sort((a, b) => a.name.localeCompare(b.name)).map((e) => renderRow(e, 0))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div style={{ fontSize: 11, opacity: 0.4, padding: "4px 0" }}>
            {entities.length} geographies · Active = community page · Inactive = location label only
          </div>
        </div>
  );

  /* ── Inline mode: render body directly (no overlay/modal chrome) ── */
  if (mode === "inline") return bodyContent;

  /* ── Modal mode: wrap in overlay + modal container ── */
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(ev) => ev.stopPropagation()}>
        {/* Header */}
        <div style={S.header}>
          <div>
            <div style={S.title}>{t("title")}</div>
            <div style={S.subtitle}>
              System-level geographic hierarchy — Earth → Countries → States → Cities.
              Active geographies generate community pages.
            </div>
          </div>
          <CloseButton onClick={onClose} size="md" />
        </div>
        {bodyContent}
      </div>
    </div>
  );
}
